import { supabase } from '@/integrations/supabase/client';
import type { SingleResult } from '@/lib/rpc/types';

export type Profile = {
  exists: boolean;
  customer_id: string | null;
  display_name: string | null;
  email: string | null;
  kyc_status: string | null;
  tos_status: string | null;
  provider_customer_id?: string | null;
};

/**
 * Get current user's profile via wallet hub Edge Function
 * CANON: Uses wallet.user.profile action instead of direct RPC
 */
export async function getMyProfile(): Promise<SingleResult<Profile>> {
  try {
    const { data, error } = await supabase.functions.invoke('wallet', {
      method: 'POST',
      body: { action: 'user.profile' }
    });

    if (error) {
      console.error('[getMyProfile] Edge function error:', error);
      return {
        ok: false,
        error: error.message || 'Failed to get profile',
        data: null,
        loading: false,
        disabled: false,
        source: 'single'
      } as SingleResult<Profile>;
    }

    // Handle API-level errors
    if (data && data.error) {
      console.error('[getMyProfile] API error:', data.error);
      return {
        ok: false,
        error: data.error,
        data: null,
        loading: false,
        disabled: false,
        source: 'single'
      } as SingleResult<Profile>;
    }

    // Normalize response to ensure ProfileSummary compatibility
    // Handles both old format (without exists/tos_status) and new format
    const normalized: Profile = {
      exists: data?.exists ?? (data?.customer_id ? true : false),
      customer_id: data?.customer_id ?? null,
      display_name: data?.display_name ?? null,
      email: data?.email ?? null,
      kyc_status: data?.kyc_status ?? 'unknown',
      tos_status: data?.tos_status ?? 'unknown',
      provider_customer_id: data?.provider_customer_id ?? null,
    };

    return {
      ok: true,
      data: normalized,
      loading: false,
      disabled: false,
      source: 'single'
    } as SingleResult<Profile>;
  } catch (err: any) {
    console.error('[getMyProfile] Exception:', err);
    return {
      ok: false,
      error: err?.message || 'Network error',
      data: null,
      loading: false,
      disabled: false,
      source: 'single'
    } as SingleResult<Profile>;
  }
}

/**
 * Update current user's profile
 * Uses user_upsert_profile_guarded RPC function
 */
export async function updateMyProfile(updates: {
  display_name?: string;
  [key: string]: any;
}): Promise<SingleResult<Profile>> {
  try {
    const { data, error } = await supabase.rpc('user_upsert_profile_guarded', {
      _account_type: 'individual', // Default account type
      _legal_name: null,
      _full_name: updates.display_name || null,
    });

    if (error) {
      console.error('[updateMyProfile] RPC error:', error);
      return {
        ok: false,
        error: error.message || 'Failed to update profile',
        data: null,
        loading: false,
        disabled: false,
        source: 'single'
      } as SingleResult<Profile>;
    }

    // Refresh profile after update
    return getMyProfile();
  } catch (err: any) {
    console.error('[updateMyProfile] Exception:', err);
    return {
      ok: false,
      error: err?.message || 'Network error',
      data: null,
      loading: false,
      disabled: false,
      source: 'single'
    } as SingleResult<Profile>;
  }
}